import React, { useState, useEffect, useRef } from 'react';
import { Download, Loader2, Share2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { supabase } from '../lib/supabaseClient';
import { trackCardDownload, trackCardShare, trackButtonClick } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

const ShareableCard = ({ selectedPath, careerOptions, roadmapId, formData, userName }) => {
  const [selectedBust, setSelectedBust] = useState(null);
  const [bustImages, setBustImages] = useState([]);
  const [templateImage, setTemplateImage] = useState(null);
  const [templateRarity, setTemplateRarity] = useState('common'); // common, rare, epic, legendary
  const [templateDimensions, setTemplateDimensions] = useState({ width: 3456, height: 4608 });
  const [isDownloading, setIsDownloading] = useState(false);
  const [pokemonData, setPokemonData] = useState(null);
  const [isGeneratingPokemon, setIsGeneratingPokemon] = useState(false);
  const [cardWidth, setCardWidth] = useState(400); // Track actual card width for font scaling
  const [careerTitle, setCareerTitle] = useState('Explorer'); // Main career title for the card
  const [savedCardLoaded, setSavedCardLoaded] = useState(false); // Track if we've checked for saved data
  const [usingSavedCard, setUsingSavedCard] = useState(false); // Track if using saved card data
  const [isExpandedMobile, setIsExpandedMobile] = useState(false); // Track if expanded on mobile
  const cardRef = useRef(null);

  // Track card container width for responsive font sizing
  useEffect(() => {
    if (!cardRef.current) return;

    const updateCardWidth = () => {
      if (cardRef.current) {
        setCardWidth(cardRef.current.offsetWidth);
      }
    };

    // Initial measurement
    updateCardWidth();

    // Use ResizeObserver for responsive updates
    const resizeObserver = new ResizeObserver(updateCardWidth);
    resizeObserver.observe(cardRef.current);

    return () => resizeObserver.disconnect();
  }, [templateImage]);

  // ============================================================================
  // LOAD SAVED CARD DATA - Check if user already has card data saved
  // Card data is generated ONCE per user and persisted
  // ============================================================================
  useEffect(() => {
    const loadSavedCardData = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setSavedCardLoaded(true);
          return;
        }

        const response = await fetch(`${BACKEND_URL}/api/user-card-data`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.exists && result.data) {
            // Use saved card data
            setTemplateRarity(result.data.rarity || 'common');
            setPokemonData({
              personalityTitle: result.data.personalityTitle,
              personalityDesc: result.data.personalityDesc,
              destinyReading: result.data.destinyReading,
              mbtiCode: result.data.userName?.charAt(0)?.toUpperCase() || 'E'
            });
            setUsingSavedCard(true);
          }
        }
      } catch (error) {
        console.error('Error loading saved card data:', error);
      } finally {
        setSavedCardLoaded(true);
      }
    };

    loadSavedCardData();
  }, []);

  // Load bust images from the Bust folder (parallel loading for faster performance)
  useEffect(() => {
    const loadBustImages = async () => {
      try {
        const tryLoadImage = (path) => {
          return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(path);
            img.onerror = () => resolve(null);
            img.src = path;
          });
        };

        // Generate all image paths
        const imagePaths = [];
        for (let i = 1; i <= 200; i++) {
          imagePaths.push(`/bust/peep-${i}.png`);
        }

        // Load all images in parallel
        const results = await Promise.all(imagePaths.map(tryLoadImage));

        // Filter out nulls (failed loads) and sort
        const loadedImages = results
          .filter((path) => path !== null)
          .sort((a, b) => {
            const numA = parseInt(a.match(/peep-(\d+)/)?.[1] || '0');
            const numB = parseInt(b.match(/peep-(\d+)/)?.[1] || '0');
            return numA - numB;
          });

        setBustImages(loadedImages);

        if (loadedImages.length === 0) {
          console.warn('No bust images found.');
        }
      } catch (error) {
        console.error('Error loading bust images:', error);
      }
    };

    loadBustImages();
  }, []);

  // Determine template rarity using AI based on roadmap
  // SKIP if we already have saved card data
  useEffect(() => {
    // Wait until we've checked for saved card data
    if (!savedCardLoaded) return;
    // Skip if using saved card data (rarity already set)
    if (usingSavedCard) return;

    const determineRarity = async () => {
      if (!formData || !careerOptions || careerOptions.length === 0) {
        // Default based on path type if no data
        const defaultRarity = selectedPath === 'risky' ? 'epic' : selectedPath === 'adventurous' ? 'rare' : 'common';
        setTemplateRarity(defaultRarity);
        return;
      }

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          // Fallback to path-based logic
          const fallbackRarity = selectedPath === 'risky' ? 'epic' : selectedPath === 'adventurous' ? 'rare' : 'common';
          setTemplateRarity(fallbackRarity);
          return;
        }

        // Call backend to determine rarity
        const response = await fetch(`${BACKEND_URL}/api/determine-card-rarity`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            selectedPath,
            careerOptions,
            formData
          })
        });

        if (response.ok) {
          const data = await response.json();
          setTemplateRarity(data.rarity || 'common');
        } else {
          // Fallback to path-based logic
          const fallbackRarity = selectedPath === 'risky' ? 'epic' : selectedPath === 'adventurous' ? 'rare' : 'common';
          setTemplateRarity(fallbackRarity);
        }
      } catch (error) {
        console.error('Error determining rarity:', error);
        // Fallback to path-based logic
        const fallbackRarity = selectedPath === 'risky' ? 'epic' : selectedPath === 'adventurous' ? 'rare' : 'common';
        setTemplateRarity(fallbackRarity);
      }
    };

    determineRarity();
  }, [formData, careerOptions, selectedPath, savedCardLoaded, usingSavedCard]);

  // Load template image based on determined rarity
  useEffect(() => {
    if (!templateRarity) return;

    const templatePath = `/templates/${templateRarity}.png`;
    const img = new Image();
    img.onload = () => {
      setTemplateImage(templatePath);
      setTemplateDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      // Fallback to common if the determined rarity doesn't exist
      const fallbackPath = '/templates/common.png';
      const fallbackImg = new Image();
      fallbackImg.onload = () => {
        setTemplateImage(fallbackPath);
        setTemplateDimensions({ width: fallbackImg.naturalWidth, height: fallbackImg.naturalHeight });
      };
      fallbackImg.src = fallbackPath;
    };
    img.src = templatePath;
  }, [templateRarity]);

  // Set career title from career options
  useEffect(() => {
    if (careerOptions && careerOptions.length > 0) {
      // Get the first/primary career option title
      const primaryCareer = careerOptions[0];
      if (primaryCareer && primaryCareer.title) {
        setCareerTitle(primaryCareer.title);
      }
    }
  }, [careerOptions]);

  // Generate Pokemon-style description when formData changes
  // SKIP if we already have saved card data
  useEffect(() => {
    // Wait until we've checked for saved card data
    if (!savedCardLoaded) return;
    // Skip if using saved card data (pokemon data already set)
    if (usingSavedCard) return;

    if (formData && !pokemonData && !isGeneratingPokemon) {
      generatePokemonDescription();
    }
  }, [formData, savedCardLoaded, usingSavedCard]);

  // Save card data to backend after generation (only runs once)
  const saveCardData = async (rarity, pokemon) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${BACKEND_URL}/api/user-card-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          rarity: rarity,
          personalityTitle: pokemon.personalityTitle,
          personalityDesc: pokemon.personalityDesc,
          destinyReading: pokemon.destinyReading,
          userName: userName
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.saved) {
        }
      }
    } catch (error) {
      console.error('Error saving card data:', error);
    }
  };

  const generatePokemonDescription = async () => {
    if (!formData) return;

    setIsGeneratingPokemon(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // Fallback to local generation
        generateLocalPokemonData();
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/generate-pokemon-card`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ formData, careerOptions })
      });

      if (response.ok) {
        const data = await response.json();
        setPokemonData(data);
        // Save the newly generated card data (rarity + pokemon data)
        saveCardData(templateRarity, data);
      } else {
        // Fallback to local generation
        generateLocalPokemonData();
      }
    } catch (error) {
      console.error('Error generating Pokemon description:', error);
      generateLocalPokemonData();
    } finally {
      setIsGeneratingPokemon(false);
    }
  };

  const generateLocalPokemonData = () => {
    // Extract data from formData
    const extracted = formData?.extracted_career_data || formData?.conversation_data || {};
    const skills = extracted.skills || extracted.technical_skills || [];
    const values = extracted.values || [];
    const interests = extracted.interests || [];
    const dreamJobs = extracted.dream_jobs || [];
    const resumeData = formData?.resume_data || {};

    // Get user's first name for alliteration
    const firstName = (userName || 'Explorer').split(' ')[0];
    const firstLetter = firstName.charAt(0).toUpperCase();

    // Alliterative adjectives by letter, with traits they match to
    const alliterativeAdjectives = {
      'A': [
        { adj: 'Ambitious', traits: ['leadership', 'driven', 'goal', 'achieve', 'success', 'growth', 'executive'] },
        { adj: 'Analytical', traits: ['data', 'analysis', 'research', 'logic', 'problem', 'strategic', 'engineer'] },
        { adj: 'Artistic', traits: ['creative', 'design', 'art', 'visual', 'aesthetic', 'innovation'] }
      ],
      'B': [
        { adj: 'Brilliant', traits: ['smart', 'intelligent', 'bright', 'clever', 'innovative', 'research'] },
        { adj: 'Bold', traits: ['risk', 'brave', 'courageous', 'startup', 'entrepreneur', 'founder'] },
        { adj: 'Building', traits: ['create', 'develop', 'engineer', 'construct', 'product', 'software'] }
      ],
      'C': [
        { adj: 'Creative', traits: ['design', 'art', 'innovative', 'imagination', 'original', 'content'] },
        { adj: 'Curious', traits: ['learn', 'explore', 'research', 'discover', 'question', 'science'] },
        { adj: 'Coding', traits: ['software', 'developer', 'programming', 'engineer', 'tech', 'computer'] }
      ],
      'D': [
        { adj: 'Driven', traits: ['ambitious', 'motivated', 'determined', 'goal', 'success', 'achieve'] },
        { adj: 'Dynamic', traits: ['energetic', 'active', 'versatile', 'adaptable', 'change', 'fast'] },
        { adj: 'Daring', traits: ['risk', 'bold', 'brave', 'adventure', 'startup', 'entrepreneur'] }
      ],
      'E': [
        { adj: 'Empathetic', traits: ['people', 'care', 'help', 'social', 'support', 'mentor', 'counsel'] },
        { adj: 'Enterprising', traits: ['business', 'entrepreneur', 'startup', 'venture', 'founder', 'ceo'] },
        { adj: 'Energetic', traits: ['active', 'dynamic', 'enthusiastic', 'passionate', 'sports'] }
      ],
      'F': [
        { adj: 'Focused', traits: ['determined', 'goal', 'driven', 'disciplined', 'dedicated', 'precision'] },
        { adj: 'Fearless', traits: ['bold', 'brave', 'risk', 'courageous', 'daring', 'adventure'] },
        { adj: 'Future-Focused', traits: ['innovative', 'visionary', 'tech', 'modern', 'progressive', 'ai'] }
      ],
      'G': [
        { adj: 'Goal-Getting', traits: ['ambitious', 'driven', 'achieve', 'success', 'determined', 'win'] },
        { adj: 'Gifted', traits: ['talented', 'skilled', 'exceptional', 'brilliant', 'natural'] },
        { adj: 'Groundbreaking', traits: ['innovative', 'pioneer', 'revolutionary', 'creative', 'first'] }
      ],
      'H': [
        { adj: 'Hardworking', traits: ['dedicated', 'diligent', 'committed', 'persistent', 'effort'] },
        { adj: 'Hands-On', traits: ['practical', 'builder', 'engineer', 'maker', 'technical', 'craft'] },
        { adj: 'Harmonious', traits: ['team', 'collaborative', 'balance', 'peaceful', 'mediator', 'hr'] }
      ],
      'I': [
        { adj: 'Innovative', traits: ['creative', 'inventive', 'original', 'pioneer', 'tech', 'startup'] },
        { adj: 'Insightful', traits: ['analytical', 'strategic', 'wise', 'perceptive', 'consultant'] },
        { adj: 'Inspiring', traits: ['leader', 'mentor', 'motivate', 'influence', 'teach', 'coach'] }
      ],
      'J': [
        { adj: 'Joyful', traits: ['positive', 'happy', 'optimistic', 'enthusiastic', 'energy', 'people'] },
        { adj: 'Judicious', traits: ['wise', 'careful', 'strategic', 'thoughtful', 'analytical', 'legal'] }
      ],
      'K': [
        { adj: 'Keen', traits: ['sharp', 'perceptive', 'analytical', 'observant', 'smart', 'detail'] },
        { adj: 'Kinetic', traits: ['active', 'dynamic', 'energetic', 'moving', 'fast', 'sports'] }
      ],
      'L': [
        { adj: 'Leading', traits: ['leader', 'management', 'executive', 'director', 'head', 'ceo'] },
        { adj: 'Logical', traits: ['analytical', 'rational', 'systematic', 'data', 'engineering', 'math'] },
        { adj: 'Limitless', traits: ['ambitious', 'potential', 'growth', 'boundless', 'visionary'] }
      ],
      'M': [
        { adj: 'Motivated', traits: ['driven', 'ambitious', 'determined', 'passionate', 'dedicated'] },
        { adj: 'Masterful', traits: ['expert', 'skilled', 'talented', 'proficient', 'accomplished'] },
        { adj: 'Magnetic', traits: ['charismatic', 'leader', 'influential', 'people', 'social', 'sales'] }
      ],
      'N': [
        { adj: 'Notable', traits: ['exceptional', 'outstanding', 'remarkable', 'distinguished', 'award'] },
        { adj: 'Nimble', traits: ['agile', 'quick', 'adaptable', 'flexible', 'responsive', 'startup'] }
      ],
      'O': [
        { adj: 'Optimistic', traits: ['positive', 'hopeful', 'confident', 'enthusiastic', 'bright'] },
        { adj: 'Outstanding', traits: ['exceptional', 'excellent', 'remarkable', 'superior', 'top'] }
      ],
      'P': [
        { adj: 'Persistent', traits: ['determined', 'dedicated', 'committed', 'resilient', 'driven'] },
        { adj: 'Pioneering', traits: ['innovative', 'first', 'trailblazer', 'founder', 'startup'] },
        { adj: 'Passionate', traits: ['enthusiastic', 'dedicated', 'love', 'care', 'committed'] }
      ],
      'Q': [
        { adj: 'Quick-Witted', traits: ['smart', 'clever', 'intelligent', 'sharp', 'fast', 'agile'] },
        { adj: 'Quality-Focused', traits: ['excellence', 'detail', 'precision', 'careful', 'thorough'] }
      ],
      'R': [
        { adj: 'Resourceful', traits: ['creative', 'problem', 'solution', 'adaptable', 'clever'] },
        { adj: 'Rising', traits: ['growth', 'ambitious', 'potential', 'emerging', 'climbing', 'young'] },
        { adj: 'Resilient', traits: ['strong', 'persistent', 'tough', 'adaptable', 'determined'] }
      ],
      'S': [
        { adj: 'Strategic', traits: ['analytical', 'planning', 'business', 'executive', 'consultant'] },
        { adj: 'Skilled', traits: ['talented', 'proficient', 'expert', 'capable', 'competent'] },
        { adj: 'Spirited', traits: ['energetic', 'enthusiastic', 'passionate', 'lively', 'dynamic'] }
      ],
      'T': [
        { adj: 'Talented', traits: ['skilled', 'gifted', 'capable', 'proficient', 'expert', 'natural'] },
        { adj: 'Tenacious', traits: ['persistent', 'determined', 'dedicated', 'resilient', 'driven'] },
        { adj: 'Tech-Savvy', traits: ['software', 'developer', 'engineer', 'technology', 'coding', 'ai'] }
      ],
      'U': [
        { adj: 'Unstoppable', traits: ['determined', 'driven', 'persistent', 'ambitious', 'powerful'] },
        { adj: 'Uplifting', traits: ['positive', 'inspiring', 'supportive', 'encouraging', 'mentor'] }
      ],
      'V': [
        { adj: 'Visionary', traits: ['innovative', 'creative', 'future', 'strategic', 'leader', 'ceo'] },
        { adj: 'Versatile', traits: ['adaptable', 'flexible', 'multi', 'diverse', 'broad', 'generalist'] },
        { adj: 'Victorious', traits: ['successful', 'winner', 'achieve', 'accomplish', 'triumph'] }
      ],
      'W': [
        { adj: 'Wise', traits: ['intelligent', 'thoughtful', 'experienced', 'knowledgeable', 'insight'] },
        { adj: 'Winning', traits: ['successful', 'achieve', 'competitive', 'ambitious', 'driven'] }
      ],
      'X': [
        { adj: 'X-Factor', traits: ['unique', 'special', 'exceptional', 'standout', 'remarkable'] }
      ],
      'Y': [
        { adj: 'Youthful', traits: ['energetic', 'fresh', 'dynamic', 'modern', 'innovative', 'young'] },
        { adj: 'Yearning', traits: ['ambitious', 'driven', 'passionate', 'eager', 'motivated'] }
      ],
      'Z': [
        { adj: 'Zealous', traits: ['passionate', 'enthusiastic', 'dedicated', 'committed', 'fervent'] },
        { adj: 'Zen', traits: ['calm', 'balanced', 'focused', 'mindful', 'peaceful', 'wellness'] }
      ]
    };

    // Combine all user traits for matching
    const allUserTraits = [
      ...skills, ...values, ...interests, ...dreamJobs,
      ...(resumeData.industries || []),
      ...(resumeData.tech_skills || []),
      ...(resumeData.all_skills || [])
    ].map(t => String(t).toLowerCase()).join(' ');

    // Get adjectives for user's first letter
    const letterAdjectives = alliterativeAdjectives[firstLetter] || alliterativeAdjectives['A'];

    // Score each adjective based on trait matches
    let bestAdjective = letterAdjectives[0].adj;
    let bestScore = 0;

    for (const { adj, traits } of letterAdjectives) {
      let score = 0;
      for (const trait of traits) {
        if (allUserTraits.includes(trait)) score += 2;
        else if (allUserTraits.split(' ').some(w => w.startsWith(trait.substring(0, 4)))) score += 1;
      }
      if (score > bestScore) {
        bestScore = score;
        bestAdjective = adj;
      }
    }

    // If no match, pick random for that letter
    if (bestScore === 0) {
      bestAdjective = letterAdjectives[Math.floor(Math.random() * letterAdjectives.length)].adj;
    }

    // Create alliterative title
    const alliterativeTitle = `${bestAdjective} ${firstName}`;

    // Descriptions for each adjective
    const descriptions = {
      'Ambitious': 'Destined for the top. No shortcuts.',
      'Analytical': 'Sees patterns others miss.',
      'Artistic': 'Turns ideas into visual magic.',
      'Brilliant': 'Brain running at full speed.',
      'Bold': 'Leaps while others hesitate.',
      'Building': 'Creates the future daily.',
      'Creative': 'Sees masterpiece in blank canvas.',
      'Curious': 'Questions everything. Learns faster.',
      'Coding': 'Speaks fluent machine. And human.',
      'Driven': 'Goals are just checkpoints.',
      'Dynamic': 'Energy that moves mountains.',
      'Daring': 'Comfort zone? Never heard of it.',
      'Empathetic': 'Knows what you need first.',
      'Enterprising': 'Sees opportunity everywhere.',
      'Energetic': 'Powered by passion.',
      'Focused': 'Laser precision on what matters.',
      'Fearless': 'Fear is just excitement.',
      'Future-Focused': 'Already living in tomorrow.',
      'Goal-Getting': 'Sets targets. Hits them. Repeats.',
      'Gifted': 'Natural talent meets effort.',
      'Groundbreaking': 'Rewrites the rulebook.',
      'Hardworking': 'Outworks everyone in the room.',
      'Hands-On': 'Theory is nice. Building is better.',
      'Harmonious': 'Brings people together.',
      'Innovative': 'Ideas flow constantly.',
      'Insightful': 'Sees the bigger picture.',
      'Inspiring': 'Makes others believe.',
      'Joyful': 'Brings sunshine everywhere.',
      'Judicious': 'Wise choices, great outcomes.',
      'Keen': 'Sharp mind, sharper instincts.',
      'Kinetic': 'Always moving, always growing.',
      'Leading': 'Born to guide, built to inspire.',
      'Logical': 'Cuts through noise with clarity.',
      'Limitless': 'No ceiling high enough.',
      'Motivated': 'Internal fire that never dims.',
      'Masterful': 'Excellence is the only standard.',
      'Magnetic': 'People lean in when they speak.',
      'Notable': 'Stands out without trying.',
      'Nimble': 'Pivots faster than problems.',
      'Optimistic': 'Sees possibility in setbacks.',
      'Outstanding': 'Sets the bar, then raises it.',
      'Persistent': 'Obstacles are just detours.',
      'Pioneering': 'First to explore, first to win.',
      'Passionate': 'Heart fully in the game.',
      'Quick-Witted': 'Fast thinker, faster solver.',
      'Quality-Focused': 'Good enough? Never enough.',
      'Resourceful': 'MacGyver energy. Max results.',
      'Rising': 'The best is yet to come.',
      'Resilient': 'Bounces back stronger.',
      'Strategic': 'Thinks three moves ahead.',
      'Skilled': 'Expertise speaks for itself.',
      'Spirited': 'Enthusiasm is contagious.',
      'Talented': 'Natural abilities, refined daily.',
      'Tenacious': 'Grip that never loosens.',
      'Tech-Savvy': 'Digital native with superpowers.',
      'Unstoppable': 'Momentum can\'t be contained.',
      'Uplifting': 'Elevates everyone around.',
      'Visionary': 'Sees futures others can\'t.',
      'Versatile': 'Many hats, all fit perfectly.',
      'Victorious': 'Winning is a habit.',
      'Wise': 'Experience meets intuition.',
      'Winning': 'Success follows naturally.',
      'X-Factor': 'That special something.',
      'Youthful': 'Fresh perspective, boundless energy.',
      'Yearning': 'Hunger for greatness.',
      'Zealous': 'Passion impossible to ignore.',
      'Zen': 'Calm center in any storm.'
    };

    const personalityDesc = descriptions[bestAdjective] || 'Destined for greatness.';

    // Destiny one-liners
    const destinyLines = [
      'Success is inevitable. So is your coffee addiction.',
      'The future is bright. Wear shades.',
      'Greatness loading... please stand by.',
      'Your potential? Literally off the charts.',
      'Warning: may cause extreme career envy.'
    ];

    const destinyReading = destinyLines[Math.floor(Math.random() * destinyLines.length)];

    const localPokemonData = {
      mbtiCode: firstLetter,
      personalityTitle: alliterativeTitle,
      personalityDesc: personalityDesc,
      destinyReading
    };

    setPokemonData(localPokemonData);
    // Save locally generated card data too
    saveCardData(templateRarity, localPokemonData);
  };

  const handleBustSelect = (bustPath) => {
    setSelectedBust(bustPath);
  };

  const handleDownload = async () => {
    if (!cardRef.current || !selectedBust) {
      toast.error('Please select a profile image first');
      return;
    }

    // Track download attempt
    trackCardDownload(templateRarity);
    setIsDownloading(true);

    const cardElement = cardRef.current;
    // Store original styles to restore later (declared outside try for finally access)
    const originalStyle = cardElement.style.cssText;
    const originalClassName = cardElement.className;

    try {
      // Use the actual template dimensions for full-quality export
      // Templates are 3456x4608px - we'll render at a scaled size and use html2canvas scale
      const exportWidth = templateDimensions.width / 4; // 864px render width
      const exportHeight = templateDimensions.height / 4; // 1152px render height
      const canvasScale = 4; // Scale back up to original dimensions

      // Temporarily set the card to export dimensions for pixel-perfect capture
      cardElement.style.width = `${exportWidth}px`;
      cardElement.style.height = `${exportHeight}px`;
      cardElement.style.maxWidth = 'none';
      cardElement.style.position = 'absolute';
      cardElement.style.left = '-9999px';
      cardElement.style.top = '0';
      // Set font-size to scale with the export width (1% of width)
      cardElement.style.fontSize = `${exportWidth * 0.01}px`;
      // Ensure border-radius is applied
      cardElement.style.borderRadius = '3%';
      cardElement.style.overflow = 'hidden';

      // Force a reflow to apply styles
      cardElement.offsetHeight;

      // Wait for images to load
      await new Promise(resolve => setTimeout(resolve, 300));

      // Render the card at full scale for crisp output
      const canvas = await html2canvas(cardElement, {
        backgroundColor: null,
        scale: canvasScale,
        useCORS: true,
        logging: false,
        allowTaint: false,
        width: exportWidth,
        height: exportHeight,
        onclone: (clonedDoc) => {
          // Ensure cloned element has proper styles
          const clonedCard = clonedDoc.querySelector('[data-card-export]') || clonedDoc.body.firstChild;
          if (clonedCard) {
            clonedCard.style.borderRadius = '3%';
            clonedCard.style.overflow = 'hidden';

            // Fix text elements - convert em to px with proper spacing for descenders
            const textElements = clonedCard.querySelectorAll('h4, p');
            textElements.forEach(el => {
              const computedStyle = window.getComputedStyle(el);
              const currentFontSize = parseFloat(computedStyle.fontSize);

              // Set explicit pixel values for font-size
              el.style.fontSize = `${currentFontSize}px`;
              // Use generous line-height to prevent clipping descenders (g, y, p, q)
              el.style.lineHeight = '1.4';
              // Add padding to ensure text isn't cut off
              el.style.paddingBottom = '0.15em';
              // Don't clip text - let it render fully
              el.style.overflow = 'visible';
              el.style.maxWidth = '100%';
            });
          }
        }
      });

      // Restore original styles immediately
      cardElement.style.cssText = originalStyle;
      cardElement.className = originalClassName;

      // Convert to blob with maximum quality
      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error('Failed to generate image');
          return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `fig-career-card-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success('Card downloaded successfully!');
      }, 'image/png', 1.0);
    } catch (error) {
      console.error('Error downloading card:', error);
      toast.error('Failed to download card');
      // Restore styles on error
      if (cardElement) {
        cardElement.style.cssText = originalStyle;
        cardElement.className = originalClassName;
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShare = async () => {
    if (!cardRef.current || !selectedBust) {
      toast.error('Please select a profile image first');
      return;
    }

    const cardElement = cardRef.current;
    // Store original styles to restore later (declared outside try for error handling)
    const originalStyle = cardElement.style.cssText;
    const originalClassName = cardElement.className;

    try {
      // Use the actual template dimensions for full-quality export
      const exportWidth = templateDimensions.width / 4; // 864px render width
      const exportHeight = templateDimensions.height / 4; // 1152px render height
      const canvasScale = 4; // Scale back up to original dimensions

      // Temporarily set the card to exact export dimensions for pixel-perfect capture
      cardElement.style.width = `${exportWidth}px`;
      cardElement.style.height = `${exportHeight}px`;
      cardElement.style.maxWidth = 'none';
      cardElement.style.position = 'absolute';
      cardElement.style.left = '-9999px';
      cardElement.style.top = '0';
      // Set font-size to scale with the export width (1% of width)
      cardElement.style.fontSize = `${exportWidth * 0.01}px`;
      // Ensure border-radius is applied
      cardElement.style.borderRadius = '3%';
      cardElement.style.overflow = 'hidden';

      // Force a reflow to apply styles
      cardElement.offsetHeight;

      // Wait for images to load
      await new Promise(resolve => setTimeout(resolve, 300));

      // Render the card at full scale for crisp output
      const canvas = await html2canvas(cardElement, {
        backgroundColor: null,
        scale: canvasScale,
        useCORS: true,
        logging: false,
        allowTaint: false,
        width: exportWidth,
        height: exportHeight,
        onclone: (clonedDoc) => {
          // Ensure cloned element has proper styles
          const clonedCard = clonedDoc.querySelector('[data-card-export]') || clonedDoc.body.firstChild;
          if (clonedCard) {
            clonedCard.style.borderRadius = '3%';
            clonedCard.style.overflow = 'hidden';

            // Fix text elements - convert em to px with proper spacing for descenders
            const textElements = clonedCard.querySelectorAll('h4, p');
            textElements.forEach(el => {
              const computedStyle = window.getComputedStyle(el);
              const currentFontSize = parseFloat(computedStyle.fontSize);

              // Set explicit pixel values for font-size
              el.style.fontSize = `${currentFontSize}px`;
              // Use generous line-height to prevent clipping descenders (g, y, p, q)
              el.style.lineHeight = '1.4';
              // Add padding to ensure text isn't cut off
              el.style.paddingBottom = '0.15em';
              // Don't clip text - let it render fully
              el.style.overflow = 'visible';
              el.style.maxWidth = '100%';
            });
          }
        }
      });

      // Restore original styles immediately
      cardElement.style.cssText = originalStyle;
      cardElement.className = originalClassName;

      canvas.toBlob(async (blob) => {
        if (!blob) {
          toast.error('Failed to generate image for sharing');
          return;
        }

        const file = new File([blob], `fig-career-card-${Date.now()}.png`, { type: 'image/png' });
        const shareData = {
          title: 'My Career Card - Fig Careers',
          text: 'Check out my career roadmap!',
          files: [file]
        };

        if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
          try {
            await navigator.share(shareData);
            trackCardShare('native');
            toast.success('Card shared successfully!');
          } catch (error) {
            if (error.name !== 'AbortError') {
              fallbackShare(canvas);
            }
          }
        } else {
          fallbackShare(canvas);
        }
      }, 'image/png', 1.0);
    } catch (error) {
      console.error('Error sharing card:', error);
      toast.error('Failed to share card');
      // Restore styles on error
      if (cardElement) {
        cardElement.style.cssText = originalStyle;
        cardElement.className = originalClassName;
      }
    }
  };

  const fallbackShare = async (canvas) => {
    try {
      canvas.toBlob(async (blob) => {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({ 'image/png': blob })
          ]);
          trackCardShare('clipboard');
          toast.success('Card copied to clipboard!');
        } catch (clipboardError) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `fig-career-card-${Date.now()}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          trackCardShare('download_fallback');
          toast.info('Clipboard not available. Card downloaded instead.');
        }
      }, 'image/png');
    } catch (error) {
      console.error('Fallback share error:', error);
      toast.error('Sharing not available. Please download the card instead.');
    }
  };

  return (
    <div className="shareable-card-section w-full py-4">
      <div className="max-w-7xl w-[calc(100%-1rem)] sm:w-full mx-auto sm:px-0">
        
        {/* Container for mobile and desktop */}
        <div className="bg-white/95 md:bg-white/60 backdrop-blur-xl rounded-3xl p-5 md:p-10 shadow-xl border border-white/60 md:border-white/40 transition-all duration-300">
          
          {/* Mobile Header (Expandable) */}
          <div 
            className="md:hidden flex items-center justify-between cursor-pointer"
            onClick={() => setIsExpandedMobile(!isExpandedMobile)}
          >
            <h2 className="text-2xl font-black uppercase tracking-tight m-0" style={{
              fontFamily: "'Montserrat', sans-serif",
              color: '#1a1a1a',
              letterSpacing: '-0.02em',
              lineHeight: '1.1'
            }}>
              Your Shareable Career Card
            </h2>
            <div className="bg-slate-100/80 p-2 rounded-full shrink-0 ml-3 transition-transform duration-300">
              <ChevronDown className={`w-6 h-6 text-slate-800 transition-transform duration-300 ${isExpandedMobile ? 'rotate-180' : ''}`} />
            </div>
          </div>

          {/* Desktop Header (Static) */}
          <h2 className="hidden md:block text-3xl md:text-4xl font-black text-center mb-8 uppercase tracking-tight" style={{
            fontFamily: "'Montserrat', sans-serif",
            color: '#1a1a1a',
            letterSpacing: '-0.02em',
            lineHeight: '1.1'
          }}>
            Your Shareable Career Card
          </h2>

          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start mt-6 md:mt-0 ${isExpandedMobile ? 'block' : 'hidden md:grid'} animate-in fade-in slide-in-from-top-4 duration-300`}>
            {/* Left Side - Bust Image Selection */}
            <div className="flex flex-col">
            <h3 className="text-xl md:text-2xl font-black mb-5 text-left" style={{
              fontFamily: "'Montserrat', sans-serif",
              color: '#1a1a1a',
              letterSpacing: '-0.01em',
              lineHeight: '1.2'
            }}>
              Choose Your Profile Image
            </h3>

            {bustImages.length === 0 ? (
              <div className="bg-slate-50 rounded-xl p-8 border-2 border-dashed border-slate-300 text-center">
                <p className="text-slate-500 mb-2">Loading profile images...</p>
                <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-4 max-h-[600px] overflow-y-auto p-4 bg-slate-50 rounded-xl border border-slate-200">
                {bustImages.map((bustPath, index) => (
                  <div key={index} className="relative w-full pt-[100%]">
                    <button
                      onClick={() => handleBustSelect(bustPath)}
                      className={`absolute inset-0 w-full h-full rounded-lg overflow-hidden border-2 transition-all bg-white flex items-center justify-center ${
                        selectedBust === bustPath
                          ? 'border-purple-600 ring-4 ring-purple-200 scale-105 z-10'
                          : 'border-slate-300 hover:border-purple-400'
                      }`}
                    >
                      <img
                        src={bustPath}
                        alt={`Profile ${index + 1}`}
                        className="w-full h-full object-contain p-2"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      {selectedBust === bustPath && (
                        <div className="absolute inset-0 bg-purple-600/20 flex items-center justify-center">
                          <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Side - Card Preview with Pokemon Stats */}
          <div className="flex flex-col">
            <h3 className="text-xl md:text-2xl font-black mb-5 text-left" style={{
              fontFamily: "'Montserrat', sans-serif",
              color: '#1a1a1a',
              letterSpacing: '-0.01em',
              lineHeight: '1.2'
            }}>
              Card Preview
            </h3>

            {/* Card Canvas - Template image as background with text overlay */}
            {templateImage ? (
              <div
                ref={cardRef}
                data-card-export="true"
                className="relative overflow-hidden mx-auto"
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  aspectRatio: `${templateDimensions.width} / ${templateDimensions.height}`,
                  borderRadius: '3%',
                  /* Base font size scales with card width - 1% of card width */
                  fontSize: `${cardWidth * 0.01}px`,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                }}
              >
                {/* Template Image as Background */}
                <img
                  src={templateImage}
                  alt={`${templateRarity} card template`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '3%',
                    zIndex: 0
                  }}
                  crossOrigin="anonymous"
                />

                {/* User's Name - at top, white text, no shadow, slightly bigger */}
                <div
                  style={{
                    position: 'absolute',
                    top: '8.5%',
                    left: '12%',
                    right: '12%',
                    textAlign: 'center',
                    zIndex: 10
                  }}
                >
                  <h4
                    style={{
                      fontFamily: "'Montserrat', 'Arial Black', sans-serif",
                      color: '#ffffff',
                      fontSize: ((userName || 'Explorer').split(' ')[0].length > 8) ? '6em' : '7em',
                      fontWeight: 900,
                      letterSpacing: '-0.02em',
                      lineHeight: '1.1',
                      margin: 0,
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word'
                    }}
                  >
                    {(userName || 'Explorer').split(' ')[0].slice(0, 12)}
                  </h4>
                </div>

                {/* Selected Bust Overlay - centered in glass container, slightly lower */}
                {selectedBust && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '15.5%',
                      left: '17%',
                      right: '17%',
                      height: '40%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10
                    }}
                  >
                    <img
                      src={selectedBust}
                      alt="Selected Profile"
                      crossOrigin="anonymous"
                      style={{
                        maxWidth: '85%',
                        maxHeight: '90%',
                        objectFit: 'contain',
                        filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.25))'
                      }}
                    />
                  </div>
                )}

                {/* Character Title - Unique alliterative title, Gloock font, black, no shadow */}
                {pokemonData && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '63%',
                      left: '18%',
                      right: '18%',
                      textAlign: 'center',
                      zIndex: 10,
                      overflow: 'hidden'
                    }}
                  >
                    <p
                      style={{
                        fontFamily: "'Gloock', 'Georgia', serif",
                        color: '#1a1a1a',
                        fontSize: (pokemonData.personalityTitle?.length || 0) > 20 ? '3.2em' : '3.8em',
                        fontWeight: 400,
                        letterSpacing: '0.01em',
                        lineHeight: '1.2',
                        margin: 0,
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    >
                      {pokemonData.personalityTitle}
                    </p>
                  </div>
                )}

                {/* Rarity Label */}
                <div
                  style={{
                    position: 'absolute',
                    top: '71%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10
                  }}
                >
                  <p
                    style={{
                      fontFamily: "'Montserrat', sans-serif",
                      color: '#1a1a1a',
                      fontSize: '1.8em',
                      fontWeight: 500,
                      letterSpacing: '0.08em',
                      margin: 0,
                      opacity: 0.75
                    }}
                  >
                    {templateRarity}
                  </p>
                </div>

                {/* Destiny Reading - witty unique description */}
                {pokemonData && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '76%',
                      left: '18%',
                      right: '18%',
                      textAlign: 'center',
                      zIndex: 10,
                      overflow: 'hidden'
                    }}
                  >
                    <p
                      style={{
                        fontFamily: "'Georgia', serif",
                        fontStyle: 'italic',
                        color: '#1a1a1a',
                        fontSize: '1.9em',
                        lineHeight: '1.3',
                        margin: 0,
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {pokemonData.destinyReading}
                    </p>
                  </div>
                )}

                {/* Loading state */}
                {isGeneratingPokemon && !pokemonData && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/60" style={{ zIndex: 20, borderRadius: '3%' }}>
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 text-purple-500 animate-spin mx-auto mb-2" />
                      <p className="text-sm text-slate-600">Generating your stats...</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="relative w-full bg-slate-50 rounded-xl flex items-center justify-center" style={{
                aspectRatio: '3/4',
                maxWidth: '400px',
                margin: '0 auto'
              }}>
                <div className="text-center text-slate-400">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                  <p>Loading template...</p>
                </div>
              </div>
            )}


            {/* Download and Share Buttons - always visible below card */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
              <Button
                onClick={handleDownload}
                disabled={isDownloading || !selectedBust}
                size="lg"
                className="flex items-center gap-2 min-w-[160px]"
                style={{
                  background: selectedBust ? '#1a1a1a' : '#9ca3af',
                  color: 'white',
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 'bold'
                }}
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Download Card
                  </>
                )}
              </Button>

              <Button
                onClick={handleShare}
                disabled={isDownloading || !selectedBust}
                size="lg"
                variant="outline"
                className="flex items-center gap-2 min-w-[160px]"
                style={{
                  borderColor: selectedBust ? '#1a1a1a' : '#9ca3af',
                  color: selectedBust ? '#1a1a1a' : '#9ca3af',
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 'bold'
                }}
              >
                <Share2 className="w-5 h-5" />
                Share Card
              </Button>
            </div>
          </div>
        </div>

        </div>
      </div>
    </div>
  );
};

export default ShareableCard;
